/**
 * Markdown written by a model. react-markdown never renders raw HTML (it is
 * dropped, not injected), so model output cannot script the page. Links open
 * in a new tab without handing it the opener. GitHub-flavoured Markdown
 * (remark-gfm) adds tables, strikethrough, task lists and bare-URL links;
 * a table sits in its own scroll box so a wide one never widens the page.
 */

import { createElement } from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

const HEADINGS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const;

/**
 * Headings for text that sits under a page's own `h2` (a document section): h1 to h3 render as
 * `level`, deeper ones one more, so the page keeps one outline whatever the model wrote.
 */
function headingComponents(level: 3 | 4): Components {
  const components: Components = {};
  HEADINGS.forEach((tag, index) => {
    const rendered = `h${index < 3 ? level : level + 1}`;
    components[tag] = ({ node: _node, ...props }) => createElement(rendered, props);
  });
  return components;
}

/** Built once per level: a new `components` object on every render would remount the headings. */
const HEADING_COMPONENTS = { 3: headingComponents(3), 4: headingComponents(4) };

export function Markdown({ children, headingLevel }: { children: string; headingLevel?: 3 | 4 }) {
  return (
    <div className="markdown">
      <ReactMarkdown
        skipHtml
        remarkPlugins={[remarkGfm]}
        components={{
          ...(headingLevel ? HEADING_COMPONENTS[headingLevel] : {}),
          a: ({ node: _node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />,
          table: ({ node: _node, ...props }) => (
            <div className="markdown-table">
              <table {...props} />
            </div>
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
